import { Injectable } from '@angular/core';
import { UtilService } from '../util/util.service';
import {
	DATE_MDY,
	DropdownItem,
	IS_NOT_NULL_OR_UNDEFINED,
	PROJECT_POSITIONS,
	PROJECT_STAGE_UUID_MAP,
	ProjectDTO,
	ProjectsTable,
	RestService,
	TO_CENTS,
	TO_DOLLARS,
} from 'cargo-chief-commons';
import { CarrierRateDialogComponent } from '@shared/carrier-rate-dialog/carrier-rate-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LastBidDialogComponent } from '../../project/last-bid-dialog/last-bid-dialog.component';
import { MaxBuyDialogComponent } from '../../project/max-buy-dialog/max-buy-dialog.component';
import { UrlService } from '../url/url.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { PROJECT_BID_LABEL } from '@domain/constants/app-variables';

@Injectable({
	providedIn: 'root',
})
export class ProjectHelperService {
	PROBABILITY_CONSTANTS = {
		LOW: 33,
		MEDIUM: 66,
	};
	PROBABILITY_LABELS = {
		LOW: 'Low',
		MEDIUM: 'Medium',
		HIGH: 'High',
	};
	private selectedCarriersReset = new BehaviorSubject<null>(null);

	constructor(
		private utilService: UtilService,
		private dialog: MatDialog,
		private urlService: UrlService,
		private restService: RestService
	) {}

	get projectActions(): any {
		return {
			dueDate: 'dueDate',
			assigneeId: 'assigneeId',
			customerDTO: 'customerDTO',
		};
	}

	get days(): Array<DropdownItem> {
		return [
			{ label: 'Monday', value: 'M' },
			{ label: 'Tuesday', value: 'T' },
			{ label: 'Wednesday', value: 'W' },
			{ label: 'Thursday', value: 'TH' },
			{ label: 'Friday', value: 'F' },
			{ label: 'Saturday', value: 'SAT' },
			{ label: 'Sunday', value: 'SUN' },
		];
	}

	convertProjectCentsToDollars(project: ProjectDTO): ProjectDTO {
		project.minRate = IS_NOT_NULL_OR_UNDEFINED(project.minRate)
			? TO_DOLLARS(project.minRate)
			: null;
		project.maxRate = IS_NOT_NULL_OR_UNDEFINED(project.maxRate)
			? TO_DOLLARS(project.maxRate)
			: null;
		project.value = IS_NOT_NULL_OR_UNDEFINED(project.value) ? TO_DOLLARS(project.value) : null;
		project.maxBuy = IS_NOT_NULL_OR_UNDEFINED(project.maxBuy) ? TO_DOLLARS(project.maxBuy) : null;
		return project;
	}

	convertProjectDollarsToCents(project: ProjectDTO): ProjectDTO {
		project.minRate = IS_NOT_NULL_OR_UNDEFINED(project.minRate) ? TO_CENTS(project.minRate) : null;
		project.maxRate = IS_NOT_NULL_OR_UNDEFINED(project.maxRate) ? TO_CENTS(project.maxRate) : null;
		project.value = IS_NOT_NULL_OR_UNDEFINED(project.value) ? TO_CENTS(project.value) : null;
		project.maxBuy = IS_NOT_NULL_OR_UNDEFINED(project.maxBuy) ? TO_CENTS(project.maxBuy) : null;
		return project;
	}

	getUpdateMsg(value: string) {
		switch (value) {
			case 'minRate':
				return 'Max Buy updated successfully.';
			case 'maxRate':
				return 'Customer Rate updated successfully.';
			case 'loadVolumeDuration':
				return 'Load Volume updated successfully.';
			case 'stageId':
				return 'Project stage updated successfully.';
			case 'loadCapacity':
				return 'Maximum capacity updated successfully.';
			case 'loadCount':
				return 'Available loads updated successfully.';
			case 'customer':
				return 'Customer updated successfully.';
			case 'position':
				return 'Position updated successfully.';
			case 'daysRun':
				return 'Days Run updated successfully.';
			case 'dueDate':
				return 'Due Date updated successfully.';
			case 'targetMargin':
				return 'Max Buy and Customer Rate updated as per your target margin.';
			case 'packaging':
				return 'Packaging updated successfully.';
			case 'trailerRequirements':
				return 'Trailer Requirements updated successfully.';
			case 'shipperNotes':
				return 'Shipper Notes updated successfully.';
			case 'receiverNotes':
				return 'Receiver Notes updated successfully.';
			case 'value':
				return 'Value updated successfully.';
			case 'commodity':
				return 'Commodity updated successfully.';
			case 'lastBid':
				return 'Last Bid updated successfully.';
			case 'loadVolume':
				return 'Load Volume Count updated successfully.';
			default:
				return 'Updated successfully';
		}
	}

	updateProjectDTOFromView(project: ProjectsTable, projectDTO: ProjectDTO): ProjectDTO {
		projectDTO.maxRate = project.maxRate;
		projectDTO.minRate = project.minRate;
		projectDTO.loadVolumeDuration = project.loadVolumeDuration;
		projectDTO.stageId = project.stageId;
		projectDTO.loadCapacity = project.loadCapacity;
		projectDTO.loadCount = project.loadCount;
		projectDTO.position = project.position;
		projectDTO.packaging = project.packaging;
		projectDTO.trailerRequirements = project.trailerRequirements;
		projectDTO.shipperNotes = project.shipperNotes;
		projectDTO.receiverNotes = project.receiverNotes;
		projectDTO.value = project.value;
		projectDTO.commodity = project.commodity;
		projectDTO.lastBid = project.lastBid;
		projectDTO = this.updateProjectDaysRun(projectDTO, project.selectedDaysRun);
		projectDTO.loadVolume = project.loadVolume;
		projectDTO.type = project.type;
		return projectDTO;
	}

	updateProjectDaysRun(projectDTO: ProjectDTO, daysRun: Array<string>): ProjectDTO {
		projectDTO.monday = daysRun.indexOf('M') > -1 ? true : false;
		projectDTO.tuesday = daysRun.indexOf('T') > -1 ? true : false;
		projectDTO.wednesday = daysRun.indexOf('W') > -1 ? true : false;
		projectDTO.thursday = daysRun.indexOf('TH') > -1 ? true : false;
		projectDTO.friday = daysRun.indexOf('F') > -1 ? true : false;
		projectDTO.saturday = daysRun.indexOf('SAT') > -1 ? true : false;
		projectDTO.sunday = daysRun.indexOf('SUN') > -1 ? true : false;
		return projectDTO;
	}

	getProjectStages(): Array<any> {
		return [
			{
				label: 'Awarded',
				type: PROJECT_STAGE_UUID_MAP.AWARDED,
			},
			{
				label: 'On Hold',
				type: PROJECT_STAGE_UUID_MAP.ON_HOLD,
			},
			{
				label: 'Re Bid',
				type: PROJECT_STAGE_UUID_MAP.RE_BID,
			},
			{
				label: 'New',
				type: PROJECT_STAGE_UUID_MAP.NEW,
			},
			{
				label: 'Live',
				type: PROJECT_STAGE_UUID_MAP.LIVE,
			},
			{
				label: 'Submitted',
				type: PROJECT_STAGE_UUID_MAP.SUBMITTED,
			},
		];
	}

	getPositions(): DropdownItem[] {
		return Object.entries(PROJECT_POSITIONS).reduce((prev, [label, value]) => {
			prev.push({ label, value });
			return prev;
		}, []);
	}

	updateProjectsList(
		projectsList: Array<ProjectDTO>,
		updatedProjects: Array<ProjectDTO>
	): Array<ProjectDTO> {
		const updatedProjectsList: Array<ProjectDTO> = projectsList.map((project) => {
			const updatedProject = updatedProjects.filter((pro) => pro.id == project.id)[0];
			if (!this.utilService.isNullOrUndefined(updatedProject)) {
				return updatedProject;
			} else {
				return project;
			}
		});
		return updatedProjectsList;
	}

	filterProjectsByStageId(
		stageIds: Array<string>,
		projectsList: Array<ProjectDTO>
	): Array<ProjectDTO> {
		if (
			!this.utilService.isNullOrUndefined(stageIds) &&
			!this.utilService.isNullOrUndefined(stageIds[0]) &&
			stageIds.length > 0
		) {
			projectsList = projectsList.filter((project) => stageIds.indexOf(project.stageId) > -1);
		}
		return projectsList;
	}

	filterProjectsByDueDate(dueDate: any, projectsList: Array<ProjectDTO>): Array<ProjectDTO> {
		if (!this.utilService.isNullOrUndefined(dueDate)) {
			projectsList = projectsList.filter((project) => project.dueDate >= dueDate);
		}
		return projectsList;
	}

	filterProjectsByAssignee(
		assigneeIds: Array<number>,
		projectsList: Array<ProjectDTO>
	): Array<ProjectDTO> {
		if (!this.utilService.isNullOrUndefined(assigneeIds) && assigneeIds.length > 0) {
			projectsList = projectsList.filter((project) => assigneeIds.indexOf(project.assigneeId) > -1);
		}
		return projectsList;
	}

	isGrossMarginValid(margin: any): boolean {
		return !this.utilService.isNullOrUndefined(margin) && margin >= 0;
	}

	isValidRateInput(inputNumber: any): boolean {
		return !this.utilService.isNullOrUndefined(inputNumber) && inputNumber.toString().length > 0;
	}

	sortByStage(projectsList: Array<ProjectsTable>, isAsc: boolean): Array<ProjectsTable> {
		return projectsList.sort(
			(a, b) =>
				0 -
				(this.utilService.isNullOrUndefined(a.stageId) >
				this.utilService.isNullOrUndefined(b.stageId)
					? 1
					: -1) *
					(isAsc ? -1 : 1)
		);
	}

	sortByTargetMargin(projectsList: Array<ProjectsTable>, isAsc: boolean): Array<ProjectsTable> {
		let withNullValue: Array<ProjectsTable> = [],
			withoutNullValues: Array<ProjectsTable> = [];
		projectsList.map((project: ProjectsTable) => {
			if (
				this.utilService.isNullOrUndefined(project.targetMargin) ||
				isNaN(project.targetMargin) ||
				project.targetMargin == Number.POSITIVE_INFINITY ||
				project.targetMargin == Number.NEGATIVE_INFINITY
			) {
				withNullValue.push(project);
			} else {
				withoutNullValues.push(project);
			}
		});
		withoutNullValues = withoutNullValues.sort(
			(a, b) => 0 - (a.targetMargin > b.targetMargin ? -1 : 1) * (isAsc ? -1 : 1)
		);
		if (isAsc) {
			return withoutNullValues.concat(withNullValue);
		} else {
			return withNullValue.concat(withoutNullValues);
		}
	}

	sortByDueDate(projectsList: Array<ProjectsTable>, isAsc: boolean): Array<ProjectsTable> {
		return projectsList.sort((a: ProjectsTable, b: ProjectsTable) => {
			return (
				(new Date(DATE_MDY(a.dueDate)).getTime() < new Date(DATE_MDY(b.dueDate)).getTime()
					? -1
					: 1) * (isAsc ? 1 : -1)
			);
		});
	}

	sortByCustomer(projectsList: Array<ProjectsTable>, isAsc: boolean): Array<ProjectsTable> {
		let withNullValue: Array<ProjectsTable> = [],
			withoutNullValues: Array<ProjectsTable> = [];
		projectsList.map((project: ProjectsTable) => {
			if (this.utilService.isNullOrUndefined(project.customerDTO)) {
				withNullValue.push(project);
			} else {
				withoutNullValues.push(project);
			}
		});
		withoutNullValues = withoutNullValues.sort(
			(a, b) =>
				0 -
				(a.customerDTO.name.toLowerCase() > b.customerDTO.name.toLowerCase() ? -1 : 1) *
					(isAsc ? -1 : 1)
		);
		if (isAsc) {
			return withoutNullValues.concat(withNullValue);
		} else {
			return withNullValue.concat(withoutNullValues);
		}
	}

	sortByProbability(projectsList: Array<ProjectsTable>, isAsc: boolean): Array<ProjectsTable> {
		let withNullValue: Array<ProjectsTable> = [],
			withoutNullValues: Array<ProjectsTable> = [];
		projectsList.map((project: ProjectsTable) => {
			if (this.utilService.isNullOrUndefined(project.probability)) {
				withNullValue.push(project);
			} else {
				withoutNullValues.push(project);
			}
		});
		withoutNullValues = withoutNullValues.sort(
			(a, b) => 0 - (a.probability > b.probability ? -1 : 1) * (isAsc ? -1 : 1)
		);
		if (isAsc) {
			return withoutNullValues.concat(withNullValue);
		} else {
			return withNullValue.concat(withoutNullValues);
		}
	}

	openCarrierRateDialog({
		hasRate,
		title,
	}: {
		hasRate: boolean;
		title: string;
	}): Promise<{ desiredPrice: number; carrierRespondedAt: string } | null> {
		const dialogRef = this.dialog.open(CarrierRateDialogComponent, {
			width: '400px',
			disableClose: true,
			data: { hasRate, title },
		});
		return dialogRef.afterClosed().toPromise();
	}

	openLastBidDialog(): Promise<{ lastBid: number } | null> {
		const dialogRef = this.dialog.open(LastBidDialogComponent, {
			width: '410px',
			disableClose: true,
		});
		return dialogRef.afterClosed().toPromise();
	}

	openMaxBuyDialog({
		project,
		title,
	}: {
		project: ProjectDTO;
		title: string;
	}): Promise<{ updatedMaxBuy: number } | null> {
		const dialogRef = this.dialog.open(MaxBuyDialogComponent, {
			width: '600px',
			disableClose: true,
			data: { project, title },
		});
		return dialogRef.afterClosed().toPromise();
	}

	getProjectProbabilityLabel(probability: number): string {
		if (!this.utilService.isNullOrUndefined(probability)) {
			if (probability <= this.PROBABILITY_CONSTANTS.LOW) {
				return this.PROBABILITY_LABELS.LOW;
			} else if (
				probability >= this.PROBABILITY_CONSTANTS.LOW &&
				probability <= this.PROBABILITY_CONSTANTS.MEDIUM
			) {
				return this.PROBABILITY_LABELS.MEDIUM;
			} else if (probability > this.PROBABILITY_CONSTANTS.MEDIUM) {
				return this.PROBABILITY_LABELS.HIGH;
			}
		}
		return '';
	}

	updateProject(project: ProjectDTO): Promise<{ data: ProjectDTO }> {
		const url = this.urlService.addProject();
		return this.restService.PUT(url, project).toPromise();
	}

	getLoadVolumeDuration(): string[] {
		return ['Day', 'Week', 'Month', 'Year'];
	}

	getLanePath(project: ProjectDTO) {
		return (
			project.originCity +
			', ' +
			project.originState +
			' -> ' +
			project.destCity +
			', ' +
			project.destState
		);
	}

	getSelectedCarriersResetEvent(): Observable<any> {
		return this.selectedCarriersReset.asObservable();
	}

	sendSelectedCarriersResetEvent() {
		this.selectedCarriersReset.next(null);
	}

	uploadData(s3Key: string) {
		const url = this.urlService.bulkProject(s3Key);
		this.restService.POST(url, {}).subscribe(
			(result) => {
				this.utilService.showSnackbar(result.message, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
			},
			(error) => {
				if (error.status == 200) {
					this.utilService.showSnackbar(error.error.text, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				} else {
					this.utilService.showSnackbar(
						error.data || 'Unable to upload now.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			}
		);
	}

	sortProjectsBoard(projectsList: Array<ProjectDTO>): Array<ProjectDTO> {
		let counterAvailableProjects = [],
			bidProjects = [],
			updateBidprojects = [];
		projectsList.map((project: ProjectDTO) => {
			if (project.bidActionLabel === PROJECT_BID_LABEL.COUNTER_AVAILABLE) {
				counterAvailableProjects.push(project);
			} else if (project.bidActionLabel === PROJECT_BID_LABEL.UPDATE_BID) {
				updateBidprojects.push(project);
			} else {
				bidProjects.push(project);
			}
		});
		return counterAvailableProjects.concat(updateBidprojects).concat(bidProjects);
	}
}
